import './App.css';

import DailyIframe from '@daily-co/daily-js';
import { DailyProvider } from '@daily-co/daily-react';
import { useCallback, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';

import api from './api';
import { getTokenFromUrl, pageUrlFromRoomUrl, roomUrlFromPageUrl } from './utils';

import AppRouter from './components/AppRouter';
import HairCheck from './components/HairCheck/HairCheck';
import HomeScreen from './components/HomeScreen/HomeScreen';
import MultipleTabsBlockedScreen from './components/MultipleTabsBlockedScreen/MultipleTabsBlockedScreen';
import { VoiceActivityProvider } from './context/VoiceActivityContext';
import { WSMessageProvider } from './context/WSMessageContext';
import useMultipleTabsDetection from './hooks/useMultipleTabsDetection';
import CheckoutPage from './pages/CheckoutPage';
import Lala from './pages/Lala';
import Sandbox from './pages/Sandbox';
import SocketTest from './pages/SocketTest';
import SupabaseTest from './pages/SupabaseTest';

/* We decide what UI to show to users based on the state of the app, which is dependent on the state of the call object. */
const STATE_IDLE = 'STATE_IDLE';
const STATE_CREATING = 'STATE_CREATING';
const STATE_JOINING = 'STATE_JOINING';
const STATE_JOINED = 'STATE_JOINED';
const STATE_LEAVING = 'STATE_LEAVING';
const STATE_ERROR = 'STATE_ERROR';
const STATE_HAIRCHECK = 'STATE_HAIRCHECK';

export default function App() {
  const [appState, setAppState] = useState(STATE_IDLE);
  const [roomUrl, setRoomUrl] = useState(null);
  const [callObject, setCallObject] = useState(null);
  const [apiError, setApiError] = useState(false);
  const [slugStatus, setSlugStatus] = useState(null); // null = verificando, 'no-slug' = sem slug, 'not-found' = slug não encontrado, 'found' = slug encontrado

  // Extrai slug da URL para detecção de múltiplas abas
  const { pathname } = typeof window !== 'undefined' ? window.location : { pathname: '' };
  const slug = pathname.replace(/^\/+|\/+$/g, '');
  const hasSlug = slug && slug.length > 0;

  // Detecta se há outra aba ativa (só checa se tiver slug)
  const { isAnotherTabActive } = useMultipleTabsDetection(slug, hasSlug);

  /**
   * Create a new call room. This function will return the newly created room URL.
   * We'll need this URL when pre-authorizing (https://docs.daily.co/reference/rn-daily-js/instance-methods/pre-auth)
   * or joining (https://docs.daily.co/reference/rn-daily-js/instance-methods/join) a call.
   *
   * NOTE: Currently disabled for security - users must access rooms via valid slug links.
   */
  // eslint-disable-next-line no-unused-vars
  const createCall = useCallback(() => {
    setAppState(STATE_CREATING);
    return api
      .createRoom()
      .then((room) => room.url)
      .catch(() => {
        setRoomUrl(null);
        setAppState(STATE_IDLE);
        setApiError(true);
      });
  }, []);

  /**
   * We've created a room, so let's start the hair check. We won't be joining the call yet.
   */
  const startHairCheck = useCallback(async (url) => {
    const newCallObject = DailyIframe.createCallObject();
    setRoomUrl(url);
    setCallObject(newCallObject);
    setAppState(STATE_HAIRCHECK);
    await newCallObject.preAuth({ url, token: getTokenFromUrl(url) }); // add a meeting token here if your room is private
    await newCallObject.startCamera();
  }, []);

  /**
   * Once we pass the hair check, we can actually join the call.
   * We'll pass the username entered during Haircheck to .join().
   */
  const joinCall = useCallback((userName) => {
    callObject.join({ url: roomUrl, userName });
  }, [callObject, roomUrl]);

  /**
   * Start leaving the current call.
   */
  const startLeavingCall = useCallback(() => {
    if (!callObject) return;
    // If we're in the error state, we've already "left", so just clean up
    if (appState === STATE_ERROR) {
      callObject.destroy().then(() => {
        setRoomUrl(null);
        setCallObject(null);
        setAppState(STATE_IDLE);
      });
    } else {
      /* This will trigger a `left-meeting` event, which in turn will trigger
      the full clean-up as seen in handleNewMeetingState() below. */
      setAppState(STATE_LEAVING);
      callObject.leave();
    }
  }, [callObject, appState]);

  /**
   * If a room's already specified in the page's URL when the component mounts,
   * join the room.
   */
  useEffect(() => {
    const loadRoomUrl = async () => {
      // Verifica se há slug na URL
      if (hasSlug) {
        const url = await roomUrlFromPageUrl();
        if (url) {
          setSlugStatus('found'); // Tem slug e encontrou a sala
          startHairCheck(url);
        } else {
          setSlugStatus('not-found'); // Tem slug mas não encontrou a sala
        }
      } else {
        setSlugStatus('no-slug'); // Não tem slug na URL
      }
    };
    loadRoomUrl();
  }, [startHairCheck, hasSlug]);

  /**
   * Update the page's URL to reflect the active call when roomUrl changes.
   */
  useEffect(() => {
    const updatePageUrl = async () => {
      const pageUrl = await pageUrlFromRoomUrl(roomUrl);
      if (pageUrl === window.location.href) return;
      window.history.replaceState(null, null, pageUrl);
    };
    updatePageUrl();
  }, [roomUrl]);

  /**
   * Update app state based on reported meeting state changes.
   *
   * NOTE: Here we're showing how to completely clean up a call with destroy().
   * This isn't strictly necessary between join()s, but is good practice when
   * you know you'll be done with the call object for a while, and you're no
   * longer listening to its events.
   */
  useEffect(() => {
    if (!callObject) return;

    const events = ['joined-meeting', 'left-meeting', 'error', 'camera-error'];

    function handleNewMeetingState() {
      switch (callObject.meetingState()) {
        case 'joined-meeting':
          setAppState(STATE_JOINED);
          break;
        case 'left-meeting':
          callObject.destroy().then(() => {
            setRoomUrl(null);
            setCallObject(null);
            setAppState(STATE_IDLE);
          });
          break;
        case 'error':
          setAppState(STATE_ERROR);
          break;
        default:
          break;
      }
    }

    // Use initial state
    handleNewMeetingState();

    /*
     * Listen for changes in state.
     * We can't use the useDailyEvent hook (https://docs.daily.co/reference/daily-react/use-daily-event) for this
     * because right now, we're not inside a <DailyProvider/> (https://docs.daily.co/reference/daily-react/daily-provider)
     * context yet. We can't access the call object via daily-react just yet, but we will later in Call.js and HairCheck.js!
     */
    events.forEach((event) => callObject.on(event, handleNewMeetingState));

    // Stop listening for changes in state
    return () => {
      events.forEach((event) => callObject.off(event, handleNewMeetingState));
    };
  }, [callObject]);

  /**
   * Show the call UI if we're either joining, already joined, or have encountered
   * an error that is _not_ a room API error.
   */
  const showCall = !apiError && [STATE_JOINING, STATE_JOINED, STATE_ERROR].includes(appState);

  /* When there's no problems creating the room and startHairCheck() has been successfully called,
   * we can show the hair check UI. */
  const showHairCheck = !apiError && appState === STATE_HAIRCHECK;

  const renderApp = () => {
    // Se detectar outra aba ativa, mostra tela de bloqueio (antes de qualquer coisa)
    if (hasSlug && isAnotherTabActive) {
      return <MultipleTabsBlockedScreen />;
    }

    // If something goes wrong with creating the room.
    if (apiError) {
      return (
        <div className="api-error">
          <h1>Erro</h1>
          <p>
            Não foi possível criar a sala. Verifique se as configurações estão corretas.
          </p>
        </div>
      );
    }

    if (showHairCheck || showCall) {
      return (
        <DailyProvider callObject={callObject}>
          <VoiceActivityProvider>
            <WSMessageProvider>
              {showHairCheck ? (
                // No API errors? Let's check our hair then.
                <HairCheck joinCall={joinCall} cancelCall={startLeavingCall} />
              ) : (
                <AppRouter leaveCall={startLeavingCall} />
              )}
            </WSMessageProvider>
          </VoiceActivityProvider>
        </DailyProvider>
      );
    }

    // The default view is the HomeScreen.
    // Se não houver slug na URL, mostra apenas mensagem amigável sem botões
    return <HomeScreen slugStatus={slugStatus} />;
  };

  return (
    <Routes>
      <Route path="/checkout/:slug" element={<CheckoutPage onMount={startLeavingCall} />} />
      <Route path="/sandbox" element={<Sandbox />} />
      <Route path="/testing" element={
        <WSMessageProvider>
          <Lala />
        </WSMessageProvider>
      } />
      <Route path="/socket-test" element={<SocketTest />} />
      <Route path="/supabase-test" element={<SupabaseTest />} />
      <Route path="*" element={
        <div className="app">
          {/* <Header /> */}
          {renderApp()}
        </div>
      } />
    </Routes>
  );
}
